import { useState, useEffect, useContext } from 'react';
import { Grid, Box, Switch, FormControlLabel } from '@mui/material';
import PaymentIcon from '@mui/icons-material/Payment';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import AdjustIcon from '@mui/icons-material/Adjust';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttLabelDate,
    SttList,
    SttPaymentList,
} from '../../../sporttia/all';
import { useToggle } from '../../../lib/hooks';
import PaymentCreationDialog from '../PaymentCreationDialog';
import { SttButton } from '../../../sporttia/buttons/SttButton';
import UseBonoDialog from '../UseBonoDialog';
import BoletoDialog from '../BoletoDialog';
import DateBuilder from '../../../lib/DateBuilder';
import { PERMISSIONS } from '../../../lib/permissions';
import { usePermission } from '../../../lib/usePermission';
import { Booking } from './BookingDialog';
import type { Payment } from '../../../types/models/Payment';
import { DefaultListResponse200 } from '../../../types/api/utils';

type Boleto = {
    id: number;
    user: {
        id: number;
        name: string;
    };
    bono: {
        id: number;
        name: string;
        price: number;
    };
};

type BonoMov = {
    id: number;
    created: string;
    concept: string;
    boleto: Boleto;
};

type BookingDialogPaymentsProps = {
    booking: Booking;
    onUpdate?: (booking?: Booking) => void;
};

export default function BookingDialogPayments({
    booking,
    onUpdate,
}: BookingDialogPaymentsProps) {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const { can } = usePermission();
    const [payments, setPayments] =
        useState<DefaultListResponse200<Payment> | null>(null);
    const [bonoMovs, setBonoMovs] =
        useState<DefaultListResponse200<BonoMov> | null>(null);
    const [creatingPayment, setCreatingPayment] = useState(false);
    const [useBoleto, setUseBoleto] = useState(false);
    const [openedBoleto, setOpenedBoleto] = useState<Boleto | null>(null);

    const [trash, toggleTrash] = useToggle(undefined, false);

    /**
     * Load payments
     */
    const loadPayments = () => {
        cxt?.api<DefaultListResponse200<Payment>>(
            'GET',
            `/bookings/${booking.id}/payments`,
            {
                params: {
                    page: 1,
                    rows: 100,
                    trash,
                    returnChildPayments: true,
                },
                success: (r: any) => {
                    setPayments(r);
                },
            },
        );
    };

    /**
     * Load bono movs
     */
    const loadBonoMovs = () => {
        cxt?.api<DefaultListResponse200<BonoMov>>(
            'GET',
            `/bookings/${booking.id}/bonos/movs`,
            {
                params: { page: 1, rows: 100, trash },
                success: (r: any) => {
                    setBonoMovs(r);
                },
            },
        );
    };

    /**
     * Booking changes
     */
    useEffect(() => {
        if (booking && booking.id) {
            loadPayments();
            loadBonoMovs();
        }
    }, [booking]);

    /**
     * Trash changes
     */
    useEffect(() => {
        if (booking && booking.id) {
            loadPayments();
            loadBonoMovs();
        }
    }, [trash]);

    /**
     * Close booking
     */
    const closeBooking = () => {
        cxt?.api<{ booking: Booking }>('PUT', `/bookings/${booking.id}/close`, {
            confirmation: true,
            success: (r: any) => {
                if (onUpdate) onUpdate(r.booking);
            },
        });
    };

    // -----| Render |-----
    return (
        <>
            <Box
                mb={3}
                display="flex"
                justifyContent="space-between"
                flexWrap="wrap"
            >
                <Box display="flex">
                    {can(PERMISSIONS.ACCOUNTING.CREATE) && (
                        <Box mr={1}>
                            <SttButton
                                color="primary"
                                variant="contained"
                                icon={<PaymentIcon />}
                                caption={t('AddPayment')}
                                onClick={() => setCreatingPayment(true)}
                            />
                        </Box>
                    )}
                    {can(PERMISSIONS.VOUCHER.MODIFY) && (
                        <Box mr={1}>
                            <SttButton
                                color="primary"
                                variant="contained"
                                icon={<ConfirmationNumberIcon />}
                                caption={t('UseFare')}
                                onClick={() => setUseBoleto(true)}
                            />
                        </Box>
                    )}
                    {can(PERMISSIONS.BOOKING.MODIFY) && (
                        <Box mr={1}>
                            <SttButton
                                color="primary"
                                variant="contained"
                                icon={<AdjustIcon />}
                                caption={t('Close')}
                                onClick={() => closeBooking()}
                            />
                        </Box>
                    )}
                </Box>
                <FormControlLabel
                    control={
                        <Switch
                            checked={
                                undefined /* We don't care about this value because the switch has its own state and this will always be false first */
                            }
                            onChange={(ev: any) =>
                                toggleTrash(ev.target.checked)
                            }
                        />
                    }
                    label={t('Trash')}
                />
            </Box>

            {/* --- Payments --- */}
            <Box>
                {payments && payments.rows && (
                    <SttPaymentList
                        clickable
                        paper={false}
                        payments={
                            payments.rows as Parameters<
                                typeof SttPaymentList
                            >[0]['payments']
                        }
                        onClose={loadPayments}
                    />
                )}
            </Box>

            {/* --- Bonos --- */}
            {bonoMovs && bonoMovs.rows && (
                <Box mt={2}>
                    <Box mt={2}>
                        <SttList
                            paper={false}
                            title={t('Bonos')}
                            onSelect={(bonoMov: BonoMov) =>
                                setOpenedBoleto(bonoMov.boleto)
                            }
                            data={bonoMovs.rows.map((bonoMov: any) => ({
                                caption: (
                                    <Grid container spacing={3}>
                                        <Grid size={{ md: 6 }}>
                                            {bonoMov.boleto.user.name}
                                            <Box
                                                style={{
                                                    fontSize: 13,
                                                    color: '#aaa',
                                                }}
                                            >
                                                <b>
                                                    <SttLabelDate
                                                        date={bonoMov.created}
                                                        timezoneName={
                                                            cxt?.sc?.timezone
                                                                ?.name
                                                        }
                                                    />
                                                </b>
                                                , {bonoMov.concept},{' '}
                                                {bonoMov.boleto.bono.name},{' '}
                                                {new DateBuilder(
                                                    bonoMov.created,
                                                    cxt?.sc?.timezone?.name,
                                                    cxt?.lang?.key,
                                                ).dmyhm()}
                                            </Box>
                                        </Grid>
                                    </Grid>
                                ),
                                objToSelect: bonoMov,
                            }))}
                        />
                    </Box>
                </Box>
            )}

            <PaymentCreationDialog
                open={creatingPayment}
                // En este caso particular fullName viene en 'user' en vez de en 'mship' (que es donde nos hace falta)
                // Además, extrañamente necesitaremos la id del user en un objeto mship.user...
                mship={
                    booking.user
                        ? ({
                              ...booking.user.mship,
                              fullName: booking.user.fullName,
                              user: { id: booking.user.id },
                          } as Parameters<
                              typeof PaymentCreationDialog
                          >[0]['mship'])
                        : undefined
                }
                // TODO: mover 'fullName' al mship, como en otras partes (requiere backend)
                targetAPI={`/bookings/${booking.id}/payments`}
                onPaymentSuccess={() => {
                    loadPayments();
                    setCreatingPayment(false);
                }}
                onClose={() => setCreatingPayment(false)}
            />

            <UseBonoDialog
                open={useBoleto}
                defaultMship={booking.user}
                bookingId={booking.id}
                onClose={() => {
                    setUseBoleto(false);
                    loadBonoMovs();
                }}
            />

            {openedBoleto && (
                <BoletoDialog
                    item={
                        openedBoleto as Parameters<
                            typeof BoletoDialog
                        >[0]['item']
                    }
                    onClose={() => setOpenedBoleto(null)}
                />
            )}
        </>
    );
}
